import { FaArrowLeft, FaCheck, FaWindowClose } from "react-icons/fa";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import { Link } from "react-router-dom";
import Button from "./Button";
import Card from "./Card";
import PageHeader from "./PageHeader";
import UserForm from "./UserForm";

export default function UserDetailView({
  headerTitle,
  backHref,
  isNewUser,
  user,
  stats,
  editing,
  onEdit,
  onSave,
  onCancel,
  onResetPassword,
  formData,
  onChange,
  showVerifyCheckbox,
  loading,
  showStats = true,
  showStatus = true,
  canEdit = true,
  canResetPassword = true,
  isAdminForm = true,
}) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {backHref && (
        <div className="mb-6">
          <Link
            to={backHref}
            className="text-primary hover:opacity-80 flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      )}

      <PageHeader
        title={headerTitle}
        action={
          canEdit && (
            <div className="flex gap-2">
              {!editing && !isNewUser && (
                <>
                  <Button onClick={onEdit}>Edit</Button>
                  {canResetPassword && (
                    <Button onClick={onResetPassword} variant="secondary">
                      Reset Password
                    </Button>
                  )}
                </>
              )}
              {(isNewUser || editing) && (
                <>
                  <Button
                    onClick={onSave}
                    variant="secondary"
                    disabled={loading}
                  >
                    {isNewUser ? "Create User" : "Save"}
                  </Button>
                  <Button
                    onClick={onCancel}
                    variant="outline"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          )
        }
      />

      <Card>
        <UserForm
          formData={formData}
          onChange={onChange}
          isAdminForm={isAdminForm}
          isNewUser={isNewUser}
          showVerifyCheckbox={showVerifyCheckbox}
          disabled={!editing && !isNewUser}
        />
      </Card>

      {!isNewUser && showStatus && user && (
        <Card className="mt-8">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text opacity-80">
                Status
              </label>
              <div className="mt-2 space-y-2">
                <p className="text-text flex items-center gap-2">
                  {user.isVerified ? (
                    <>
                      <FaCheck className="text-secondary" /> Verified
                    </>
                  ) : (
                    <>
                      <FaWindowClose className="text-accent" /> Not Verified
                    </>
                  )}
                </p>
                {user.metadata?.banned && (
                  <p className="text-accent flex items-center gap-2">
                    <HiOutlineExclamationTriangle /> Banned
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {!isNewUser && user?.role === "tutor" && user?.profile && !editing && (
        <Card className="mt-8">
          <h3 className="font-semibold mb-4 text-text">Tutor Profile</h3>
          <div className="space-y-4">
            {user.profile.bio && (
              <div>
                <label className="block text-sm font-medium text-text opacity-80 mb-1">
                  Bio
                </label>
                <p className="text-text">{user.profile.bio}</p>
              </div>
            )}
            {user.profile.subjects && user.profile.subjects.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-text opacity-80 mb-1">
                  Subjects
                </label>
                <div className="space-y-2">
                  {user.profile.subjects.map((subject, idx) => {
                    const subjectName =
                      typeof subject.subjectId === "object"
                        ? subject.subjectId?.name
                        : subject.subjectId;
                    const subjectCode =
                      typeof subject.subjectId === "object"
                        ? subject.subjectId?.code
                        : "";
                    return (
                      <p key={idx} className="text-text">
                        {subjectName} {subjectCode && `(${subjectCode})`} -{" "}
                        {subject.expertiseLevel}
                      </p>
                    );
                  })}
                </div>
              </div>
            )}
            {user.profile.availability &&
              user.profile.availability.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-text opacity-80 mb-1">
                    Availability
                  </label>
                  <div className="space-y-1">
                    {user.profile.availability.map((slot, idx) => (
                      <p key={idx} className="text-text text-sm">
                        {
                          [
                            "Sunday",
                            "Monday",
                            "Tuesday",
                            "Wednesday",
                            "Thursday",
                            "Friday",
                            "Saturday",
                          ][slot.dayOfWeek]
                        }
                        : {slot.startTime} - {slot.endTime}
                      </p>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </Card>
      )}

      {!isNewUser && showStats && stats && (
        <Card className="mt-8">
          <h3 className="font-semibold mb-4 text-text">User Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-text opacity-70">Total Bookings</p>
              <p className="text-lg font-bold text-text">{stats.bookings}</p>
            </div>
            <div>
              <p className="text-sm text-text opacity-70">Total Sessions</p>
              <p className="text-lg font-bold text-text">{stats.sessions}</p>
            </div>
            {user.role === "student" && (
              <>
                <div>
                  <p className="text-sm text-text opacity-70">
                    Active Penalties
                  </p>
                  <p className="text-lg font-bold text-text">
                    {stats.penalties}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text opacity-70">
                    Total Penalty Points
                  </p>
                  <p className="text-lg font-bold text-text">
                    {stats.totalPenaltyPoints}
                  </p>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
